import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiDownload, FiCopy, FiCheck } = FiIcons;

const ExcelTemplateGenerator = ({ onBack, countries }) => {
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [copied, setCopied] = useState(false);

  const formats = [
    { id: 'csv', name: 'CSV Template', description: 'Comma-separated values file' },
    { id: 'json', name: 'JSON Template', description: 'JavaScript Object Notation' },
    { id: 'excel', name: 'Excel Template', description: 'Excel spreadsheet (.xlsx)' }
  ];

  // Updated templates without banner images
  const csvTemplate = `country_code,brand,title,description,discount,code,logo_image,store_url
SA,Brand Name,Offer Title,Description of the offer,20%,CODE123,https://example.com/logo.png,https://www.potterybarnkids.com.sa/en/
AE,Another Brand,Another Title,Another description,30%,AECODE,https://example.com/logo2.png,https://www.potterybarnkids.com.sa/en/store`;

  const jsonTemplate = `{
  "SA": [
    {
      "brand": "Brand Name",
      "title": "Offer Title",
      "description": "Description of the offer",
      "discount": "20%",
      "code": "CODE123",
      "logoImage": "https://example.com/logo.png",
      "storeUrl": "https://www.potterybarnkids.com.sa/en/"
    }
  ],
  "AE": [
    {
      "brand": "Another Brand",
      "title": "Another Title",
      "description": "Another description",
      "discount": "30%",
      "code": "AECODE",
      "logoImage": "https://example.com/logo2.png",
      "storeUrl": "https://www.potterybarnkids.com.sa/en/store"
    }
  ]
}`;

  const getTemplateContent = () => {
    return selectedFormat === 'csv' ? csvTemplate : jsonTemplate;
  };

  const downloadTemplate = async () => {
    const content = getTemplateContent();
    
    if (selectedFormat === 'excel') {
      try {
        // Dynamically import xlsx
        const XLSX = await import('xlsx');
        
        // Parse CSV to create worksheet
        const rows = csvTemplate.split('\n').map(row => row.split(','));
        const headers = rows[0];
        const data = rows.slice(1).map(row => {
          const obj = {};
          headers.forEach((header, i) => {
            obj[header] = row[i];
          });
          return obj;
        });
        
        // Create workbook and worksheet
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Coupons');
        
        // Generate and download file
        XLSX.writeFile(workbook, 'coupon_template.xlsx');
      } catch (error) {
        console.error("Excel download failed:", error);
        alert("Failed to generate Excel file. Please try another format.");
      }
      return;
    }
    
    // For CSV and JSON
    const fileExtension = selectedFormat === 'csv' ? 'csv' : 'json';
    const blob = new Blob([content], { type: `text/${fileExtension}` });
    
    try {
      // Create a download link
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `coupon_template.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const copyTemplate = () => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = getTemplateContent();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy template:', err);
    }
  };

  return (
    <div className="p-5">
      <div className="flex items-center mb-5">
        <button
          onClick={onBack}
          className="mr-3 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="text-lg" />
        </button>
        <h2 className="text-xl font-bold text-gray-800">Download Template</h2>
      </div>
      
      <div className="mb-5">
        <p className="text-gray-600 text-sm mb-3">
          Download a template file to help you format your coupon data correctly. The template includes the required columns with example data.
        </p>
        
        <div className="bg-blue-50 p-3 rounded-lg mb-4">
          <h3 className="font-medium text-blue-800 mb-1">Available Country Codes</h3>
          <div className="flex flex-wrap gap-2">
            {countries.map(country => (
              <div
                key={country.code}
                className="flex items-center space-x-1 bg-white px-2 py-1 rounded border border-blue-100"
              >
                <span>{country.flag}</span>
                <span className="text-sm font-mono">{country.code}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-blue-50 p-3 rounded-lg mb-4">
          <h3 className="font-medium text-blue-800 mb-1">Important Notes</h3>
          <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
            <li>Store URLs must be in the format: <code className="bg-white px-1 py-0.5 rounded">https://www.potterybarnkids.com.sa/en/</code></li>
            <li>Logo images should be provided as direct URLs to image files</li>
            <li>Logo images will also be automatically fetched from website favicons if not provided</li>
            <li>All URLs must include the protocol (http:// or https://)</li>
          </ul>
        </div>
      </div>
      
      <div className="space-y-3 mb-5">
        <p className="font-medium text-gray-700">Select Format:</p>
        {formats.map(format => (
          <button
            key={format.id}
            onClick={() => setSelectedFormat(format.id)}
            className={`w-full flex items-start p-4 rounded-lg border ${
              selectedFormat === format.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:bg-gray-50'
            } transition-colors`}
          >
            <div className="flex-1">
              <p className="font-medium text-gray-800">{format.name}</p>
              <p className="text-sm text-gray-500">{format.description}</p>
            </div>
            <div
              className={`w-5 h-5 rounded-full ${
                selectedFormat === format.id
                  ? 'bg-blue-500'
                  : 'border-2 border-gray-300'
              } flex items-center justify-center`}
            >
              {selectedFormat === format.id && (
                <div className="w-2 h-2 bg-white rounded-full"></div>
              )}
            </div>
          </button>
        ))}
      </div>
      
      {selectedFormat !== 'excel' && (
        <div className="bg-gray-50 p-4 rounded-lg mb-5 overflow-auto max-h-40">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap">
            {getTemplateContent()}
          </pre>
        </div>
      )}
      
      <div className="flex space-x-3">
        <button
          onClick={downloadTemplate}
          className="flex-1 py-3 rounded-xl font-semibold text-white bg-blue-500 hover:bg-blue-600 flex items-center justify-center space-x-2 transition-colors"
        >
          <SafeIcon icon={FiDownload} />
          <span>Download</span>
        </button>
        
        {selectedFormat !== 'excel' && (
          <button
            onClick={copyTemplate}
            className={`flex-1 py-3 rounded-xl font-semibold ${
              copied
                ? 'text-green-600 bg-green-50'
                : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
            } flex items-center justify-center space-x-2 transition-colors`}
          >
            <SafeIcon icon={copied ? FiCheck : FiCopy} />
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ExcelTemplateGenerator;